/**
 * Textos de `partner` en español: el Programa de socios (`/socios`). La
 * fuente; `en/partner.ts` tiene que tener las mismas claves.
 *
 * Los montos llegan como argumento ya formateados ("USD 5"): la economía del
 * programa es del backend y no se escribe a mano en ningún texto fuera de los
 * que la explican.
 */
const partner = {
  scope: 'TBit Jobs',
  title: 'Programa de socios',
  lede: 'Recomienda TBit Jobs con tu código y gana por cada persona que se suscriba.',
  loading: 'Cargando el programa de socios',
  loadError: 'No pudimos cargar el programa. Recarga la página.',

  /** Cómo funciona, arriba del formulario. */
  how: {
    scope: 'Cómo funciona',
    discount: 'Quien se suscriba con tu código o tu enlace tiene USD 7 de descuento en su primer mes.',
    commission: 'Tú recibes USD 5 por cada mes que esa persona pague, mientras siga suscrita.',
    /** Las reglas completas: los términos del programa, publicados en tbit.app. */
    terms: { label: 'Lee los Términos del Programa de socios', href: 'https://tbit.app/es/legal/tbit-jobs-programa-de-socios' },
    payouts: 'Lo que ganas se te acredita cuando tu referido paga, y Stripe lo deposita en tu cuenta de banco según tu calendario de depósitos, normalmente una vez al mes (en algunos países con un monto mínimo). Para eso, cuando te aprobemos, configuras tus cobros en un formulario de Stripe.',
  },

  form: {
    scope: 'Tu solicitud',
    title: 'Quiero ser socio',
    name: 'Tu nombre',
    email: 'Tu correo',
    emailHint: 'Es el de tu sesión. Ahí te avisamos cuando revisemos tu solicitud.',
    whatsapp: 'Tu WhatsApp',
    whatsappHint: 'Con el código de país, por ejemplo +57 300 123 4567.',
    country: 'País donde vas a recibir los pagos',
    countryPlaceholder: 'Elige tu país',
    countryHint: 'Después no se puede cambiar: es el país de tu cuenta de cobros.',
    countriesWePay: 'Podemos pagarte en:',
    countryMissing: '¿Tu país no aparece? Por ahora Stripe no nos deja pagar en Honduras, Venezuela, Nicaragua ni Brasil. Escríbenos y buscamos una alternativa.',
    community: 'Tu agencia o comunidad (opcional)',
    communityPlaceholder: 'Por ejemplo, Visas H2B Colombia',
    plan: '¿Cómo vas a promocionar TBit Jobs?',
    planPlaceholder: 'Cuéntanos dónde y a quién: tu grupo de Facebook, tu canal de YouTube, tu oficina…',
    code: 'El código que quieres',
    codeHint: 'Solo letras y números, de 3 a 24. Es tu cupón y va en tu enlace.',
    submit: 'Enviar mi solicitud',
    /**
     * La casilla OBLIGATORIA de los términos del programa, encima del botón.
     * El servidor no guarda la solicitud sin ella (y guarda la versión aceptada).
     */
    terms: {
      before: 'Acepto los ',
      label: 'Términos del Programa de socios de TBit Jobs',
      href: 'https://tbit.app/es/legal/tbit-jobs-programa-de-socios',
      after: '.',
      required: 'Marca la casilla para aceptar los términos del programa.',
      outdated: 'Actualizamos los términos. Léelos y vuelve a marcar la casilla.',
    },
    submitting: 'Enviando…',
    errors: {
      name: 'Escribe tu nombre.',
      whatsapp: 'Revisa tu WhatsApp: tiene que llevar el código de país.',
      country: 'Elige un país de la lista.',
      community_name: 'Revisa el nombre de tu agencia o comunidad.',
      promotion_plan: 'Cuéntanos un poco más (al menos una frase).',
      code: 'Usa solo letras y números, de 3 a 24.',
      codeTaken: 'Ese código ya lo tiene alguien. Prueba con otro.',
      failed: 'No pudimos enviar tu solicitud. Inténtalo de nuevo en un momento.',
    },
  },

  pending: {
    title: 'Recibimos tu solicitud',
    body: (code: string) =>
      `La estamos revisando. Te escribimos al correo cuando esté lista. El código que pediste es ${code}.`,
  },

  rejected: {
    title: 'Tu solicitud no fue aprobada',
    reason: (reason: string) => `Motivo: ${reason}`,
    retry: 'Si cambió algo, puedes corregirla y enviarla de nuevo.',
  },

  panel: {
    paused:
      'Tu código está en pausa: no da descuento a personas nuevas, y las mensualidades que se paguen durante la pausa no generan comisión. Lo que ya ganaste se te sigue pagando. Escríbenos si tienes dudas.',
    terminated:
      'Tu participación en el programa terminó. Te pagamos las comisiones que causaste mientras estuviste activo, cuando tu cuenta de cobros esté habilitada.',
    /** Retención de pagos (sección 7 de los términos). El motivo lo da el equipo, no la pantalla. */
    payoutsHeld: 'Retuvimos tus pagos mientras revisamos tu cuenta. Escríbenos a support@tbit.app.',
    /**
     * Re-aceptar los términos: el socio no aceptó la versión vigente (entró
     * antes de la casilla, o los términos cambiaron).
     */
    terms: {
      title: (version: string) => `Acepta los Términos del Programa de socios (versión ${version})`,
      body: 'Para seguir en el programa, lee y acepta la versión vigente de los términos. Un cambio de términos nunca altera las condiciones de los referidos que ya trajiste.',
      accept: 'Aceptar los términos',
      accepting: 'Guardando…',
      failed: 'No pudimos guardar tu aceptación. Inténtalo de nuevo en un momento.',
    },
    codeScope: 'Tu código',
    linkLabel: 'Tu enlace',
    copy: 'Copiar enlace',
    copied: 'Copiado',
    shareNote: 'Quien entre por tu enlace o escriba tu código al pagar queda contigo, con USD 7 de descuento en su primer mes.',
    payouts: {
      scope: 'Tus cobros',
      none: 'Todavía no configuras tus cobros. Lo que ganes queda guardado y te lo pagamos apenas termines.',
      pending: 'Stripe todavía está revisando tus datos o te falta completar el formulario.',
      ready: 'Listo. Lo que ganas se te acredita cuando cada persona que trajiste paga su mes, y Stripe lo deposita en tu banco según tu calendario de depósitos.',
      setup: 'Configurar mis cobros',
      continue: 'Continuar con Stripe',
      dashboard: 'Ver mis pagos en Stripe',
      opening: 'Abriendo…',
      failed: 'No pudimos abrir Stripe. Inténtalo de nuevo en un momento.',
      unsupportedCountry: 'Stripe no puede pagar en el país de tu solicitud. Escríbenos y lo resolvemos contigo.',
      status: {
        none: 'Sin configurar',
        pending: 'Pendiente',
        ready: 'Listo',
      },
    },
    totals: {
      paid: 'Pagado',
      pending: 'Por pagar',
      failed: 'Reintentando',
    },
    referrals: {
      scope: 'Tus referidos',
      count: (n: number) => (n === 1 ? '1 persona' : `${n} personas`),
      /** El contador de arriba: cuántos se registraron con tu código. */
      registered: (n: number) =>
        n === 1 ? '1 persona se registró con tu código' : `${n} personas se registraron con tu código`,
      /** Para qué está esta lista: acompañarlos, no venderles por fuera. */
      followUp: 'Escríbeles para ayudarles a terminar su hoja de vida o a empezar su prueba. Ellos saben que ves su correo y el estado de su plan.',
      empty: 'Todavía nadie se registra con tu código.',
      emptyNote: 'Comparte tu enlace: cada persona que se registre aparece acá.',
      /** Con un filtro elegido que no tiene a nadie. */
      emptyFilter: 'Nadie en este estado por ahora.',
      /** Socio en pausa o terminado: no ve la lista. */
      hidden:
        'Mientras tu código está en pausa, o si tu participación terminó, no ves la lista de tus referidos. Tus cobros y comisiones siguen acá, y lo que ganaste se te sigue pagando.',
      filterLabel: 'Filtrar referidos por estado',
      all: 'Todos',
      columns: { name: 'Nombre', email: 'Correo', registered: 'Registro', state: 'Estado', terms: 'Contrato' },
      /** La versión de los términos con la que entró: «v. 2026-10-08». */
      termsVersion: (version: string) => `v. ${version}`,
      termsNote: 'Contrato: la versión de los términos con la que entró cada persona. Sus comisiones siguen esas reglas aunque los términos cambien.',
      state: {
        paying: 'Pagando',
        trial: 'En prueba',
        no_trial: 'Sin prueba activa',
        canceled: 'Canceló',
        courtesy: 'Cortesía',
      },
      /** Debajo de «En prueba». */
      trialEnds: (date: string) => `Termina el ${date}`,
      /** Por qué «Cortesía» va aparte. */
      courtesyNote: 'Cortesía: plan que le regaló TBit, sin cobro. No genera comisión.',
    },
    commissions: {
      scope: 'Tus comisiones',
      empty: 'Aún no hay comisiones. Llegan cuando una persona que trajiste paga su primer mes.',
      columns: { date: 'Fecha', amount: 'Monto', status: 'Estado' },
      status: {
        PENDING: 'Pendiente',
        PAID: 'Pagada',
        FAILED: 'Fallida, la reintentamos',
        VOID: 'Anulada',
      },
      /** Por qué una comisión quedó anulada (no se paga). */
      voidReason: {
        refunded: 'Anulada: le devolvimos el pago a esa persona',
        disputed: 'Anulada: esa persona disputó el cobro con su banco',
      } as Record<string, string>,
    },
  },
};

export default partner;
